import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import Select from 'react-select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// If you have a toast system
import { toast } from 'react-hot-toast';

// Layout & UI components (adjust paths accordingly)
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Button from '../../shared/components/ui/Button';

// === Import your API functions (adjust paths) ===
import {
  getProjectByID,
  getDynamicFields,
  getDynamicFieldsProject,
  getDynamicFieldsUnit,
  getDynamicFieldsUnitPricing,
  getAnnexures,
  createTemplate
} from '../services/api/implementation/impl';
import { useSidebar } from '../../shared/contexts/SidebarContext';

const fieldSelectStyles = {
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#2A565E'
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#fff'
  })
};

function FieldMultiSelect({ value, onChange, options, placeholder }) {
  return (
    <Select
      isMulti
      value={value}
      onChange={(next) => onChange(next || [])}
      options={options}
      placeholder={placeholder}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      styles={{
        ...fieldSelectStyles,
        menuPortal: (base) => ({ ...base, zIndex: 40 })
      }}
    />
  );
}

const NewDocumentTemplate = () => {
  const navigate = useNavigate();
  const { id: projectId } = useParams(); // e.g. /projects/:id

  const goToPropertyTemplates = () => {
    if (!projectId) {
      navigate(-1);
      return;
    }
    navigate(`/builder/management/company/projects/${projectId}`, {
      replace: true,
      state: { secondaryTab: 'Property Templates' }
    });
  };

  // ====================== 1) Fetch Project (for company_id) ======================
  const {
    data: project,
    isLoading: isLoadingProject,
    isError: isErrorProject
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProjectByID(projectId),
    enabled: !!projectId
  });

  // ====================== 2) Fetch Dynamic Fields ======================
  // Returned data looks like:
  // [
  //   { id: 1, column_name: 'CLOSED_DEAL_ID', readable_name: 'Closed Deal Id' },
  //   ...
  // ]
  const {
    data: dynamicFieldsList,
    isLoading: isLoadingDynamicFields,
    isError: isErrorDynamicFields
  } = useQuery({
    queryKey: ['dynamic-fields'],
    queryFn: getDynamicFields // or pass projectId if needed
  });

  // ====================== 3) Fetch Annexures ======================
  // Example:
  // [
  //   { id: 1, column_name: "PROJECT_SPECIFICATION", readable_name: "Project Specification" },
  //   ...
  // ]
  const {
    data: annexureList,
    isLoading: isLoadingAnnexures,
    isError: isErrorAnnexures
  } = useQuery({
    queryKey: ['annexures'],
    queryFn: getAnnexures
  });

  const {
    data: projectDetailsFieldsList,
    isLoading: isLoadingProjectDetailsFields,
    isError: isErrorProjectDetailsFields
  } = useQuery({
    queryKey: ['dynamic-fields', 'project'],
    queryFn: getDynamicFieldsProject
  });

  const {
    data: unitDetailsFieldsList,
    isLoading: isLoadingUnitDetailsFields,
    isError: isErrorUnitDetailsFields
  } = useQuery({
    queryKey: ['dynamic-fields', 'unit'],
    queryFn: getDynamicFieldsUnit
  });

  const {
    data: unitPricingDetailsFieldsList,
    isLoading: isLoadingUnitPricingDetailsFields,
    isError: isErrorUnitPricingDetailsFields
  } = useQuery({
    queryKey: ['dynamic-fields', 'unit-pricing'],
    queryFn: getDynamicFieldsUnitPricing
  });

  // ====================== Component States ======================
  const [documentName, setDocumentName] = useState('');
  const [description, setDescription] = useState('');
  const [customDetails, setCustomDetails] = useState('');
  const [includeLogo, setIncludeLogo] = useState(true);
  const [includeWatermark, setIncludeWatermark] = useState(false);
  const { collapsed } = useSidebar();
  // Multi-select states
  const [selectedDynamicFields, setSelectedDynamicFields] = useState([]);
  const [selectedProjectDetails, setSelectedProjectDetails] = useState([]);
  const [selectedUnitDetails, setSelectedUnitDetails] = useState([]);
  const [selectedUnitPricingDetails, setSelectedUnitPricingDetails] = useState([]);
  const [selectedAnnexures, setSelectedAnnexures] = useState([]);

  // ====================== Build React-Select Options ======================
  //  We'll use `readable_name` as the label, and `column_name` as the value:
  //  e.g. { value: 'CLOSED_DEAL_ID', label: 'Closed Deal Id' }
  const dynamicFieldOptions = useMemo(() => {
    if (!dynamicFieldsList || !Array.isArray(dynamicFieldsList)) return [];
    return dynamicFieldsList.map((field) => ({
      value: field.column_name,
      label: field.readable_name
    }));
  }, [dynamicFieldsList]);

  const annexureOptions = useMemo(() => {
    if (!annexureList || !Array.isArray(annexureList)) return [];
    return annexureList.map((ann) => ({
      value: ann.column_name,
      label: ann.readable_name
    }));
  }, [annexureList]);

  const projectDetailsOptions = useMemo(() => {
    if (!projectDetailsFieldsList || !Array.isArray(projectDetailsFieldsList)) return [];
    return projectDetailsFieldsList.map((field) => ({
      value: field.column_name,
      label: field.readable_name
    }));
  }, [projectDetailsFieldsList]);

  const unitDetailsOptions = useMemo(() => {
    if (!unitDetailsFieldsList || !Array.isArray(unitDetailsFieldsList)) return [];
    return unitDetailsFieldsList.map((field) => ({
      value: field.column_name,
      label: field.readable_name
    }));
  }, [unitDetailsFieldsList]);

  const unitPricingDetailsOptions = useMemo(() => {
    if (!unitPricingDetailsFieldsList || !Array.isArray(unitPricingDetailsFieldsList)) return [];
    return unitPricingDetailsFieldsList.map((field) => ({
      value: field.column_name,
      label: field.readable_name
    }));
  }, [unitPricingDetailsFieldsList]);

  // ====================== 4) Create Template Mutation ======================
  const { mutate: mutateCreateTemplate, isLoading: isCreating } = useMutation({
    mutationFn: (payload) => createTemplate(payload),
    onSuccess: () => {
      toast.success('Template created successfully!');
      goToPropertyTemplates();
    },
    onError: (error) => {
      console.error('Error creating template:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to create template');
    }
  });

  // ====================== Submit Handler ======================
  const handleSave = () => {
    // Ensure we have project data to get company_id
    if (!project) {
      toast.error('Project data not loaded yet.');
      return;
    }

    // Build payload with column_name
    const payload = {
      document_name: documentName,
      description: description,
      dynamic_fields: selectedDynamicFields.map((item) => item.value),
      project_dynamic_fields:
        selectedProjectDetails.length > 0
          ? selectedProjectDetails.map((item) => item.value)
          : null,
      unit_dynamic_fields:
        selectedUnitDetails.length > 0 ? selectedUnitDetails.map((item) => item.value) : null,
      unit_pricing_dynamic_fields:
        selectedUnitPricingDetails.length > 0
          ? selectedUnitPricingDetails.map((item) => item.value)
          : null,
      annexure: selectedAnnexures.map((item) => item.value),
      content: customDetails,
      project_id: Number(projectId),
      company_id: Number(project.company_id),
      include_logo: includeLogo,
      include_watermark: includeWatermark,
      html_template: ''
    };

    mutateCreateTemplate(payload);
  };

  // ====================== Cancel / Go Back ======================
  const handleCancel = () => {
    goToPropertyTemplates();
  };

  // ====================== Render ======================
  const isLoadingAll =
    isLoadingProject ||
    isLoadingDynamicFields ||
    isLoadingAnnexures ||
    isLoadingProjectDetailsFields ||
    isLoadingUnitDetailsFields ||
    isLoadingUnitPricingDetailsFields;
  const isErrorAll =
    isErrorProject ||
    isErrorDynamicFields ||
    isErrorAnnexures ||
    isErrorProjectDetailsFields ||
    isErrorUnitDetailsFields ||
    isErrorUnitPricingDetailsFields;

  if (isLoadingAll) {
    return <div>Loading ...</div>;
  }
  if (isErrorAll) {
    return <div>Error loading data. Please try again later.</div>;
  }

  return (
    <Layout>
      {/* If you have a custom Header */}
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          data={{ name: 'Create New Template' }}
          backArrow={true}
          onBack={goToPropertyTemplates}
        />
      </div>

      <div
        className={`fixed z-[5] top-[6rem] bottom-0 flex justify-center overflow-hidden px-4 pb-4 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <div className="flex h-full min-h-0 w-full max-w-6xl flex-col rounded-lg border border-gray-200 bg-white shadow-sm">
          {/* Page header + actions (fixed above scroll area) */}
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
            <h2 className="text-xl font-semibold">New Document Template</h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel} displayType="secondary">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isCreating}>
                {isCreating ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>

          {/* Scrollable form body */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">
          {/* Document Details */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="font-semibold">Document Name</label>
              <input
                type="text"
                className="w-full p-2 border rounded mt-1"
                placeholder="Allotment Letter..."
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
              />
            </div>
            <div>
              <label className="font-semibold">Description</label>
              <input
                type="text"
                className="w-full p-2 border rounded mt-1"
                placeholder="Short description..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
            <p className="text-sm font-semibold text-slate-700">PDF look</p>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={includeLogo}
                onChange={(e) => setIncludeLogo(e.target.checked)}
              />
              <span>
                Company logo in header
                <span className="block text-xs text-slate-500">From company settings</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={includeWatermark}
                onChange={(e) => setIncludeWatermark(e.target.checked)}
              />
              <span>
                Watermark on body
                <span className="block text-xs text-slate-500">
                  Faded logo/name behind the agreement — optional
                </span>
              </span>
            </label>
          </div>

          {/* Dynamic Fields Multi-Select */}
          <div className="mb-6">
            <label className="font-semibold block mb-2">Dynamic Fields</label>
            <FieldMultiSelect
              value={selectedDynamicFields}
              onChange={setSelectedDynamicFields}
              options={dynamicFieldOptions}
              placeholder="Select dynamic fields..."
            />
          </div>

          {/* Project Details Multi-Select */}
          <div className="mb-6">
            <label className="font-semibold block mb-2">Project Details</label>
            <FieldMultiSelect
              value={selectedProjectDetails}
              onChange={setSelectedProjectDetails}
              options={projectDetailsOptions}
              placeholder="Select project detail fields..."
            />
          </div>

          {/* Unit Details Multi-Select */}
          <div className="mb-6">
            <label className="font-semibold block mb-2">Unit Details</label>
            <FieldMultiSelect
              value={selectedUnitDetails}
              onChange={setSelectedUnitDetails}
              options={unitDetailsOptions}
              placeholder="Select unit detail fields..."
            />
          </div>

          {/* Unit Pricing Details Multi-Select */}
          <div className="mb-6">
            <label className="font-semibold block mb-2">Unit Pricing Details</label>
            <FieldMultiSelect
              value={selectedUnitPricingDetails}
              onChange={setSelectedUnitPricingDetails}
              options={unitPricingDetailsOptions}
              placeholder="Select unit pricing fields..."
            />
          </div>

          {/* Annexure Multi-Select */}
          <div className="mb-6">
            <label className="font-semibold block mb-2">Annexure (Additional Docs)</label>
            <FieldMultiSelect
              value={selectedAnnexures}
              onChange={setSelectedAnnexures}
              options={annexureOptions}
              placeholder="Select annexure documents..."
            />
          </div>

          {/* Rich Text Editor for Template Body */}
          <div className="mb-6">
            <label className="font-semibold block mb-2">Custom Details (Body)</label>
            <ReactQuill
              theme="snow"
              value={customDetails}
              onChange={setCustomDetails}
              placeholder="Type or paste the main body of your template here..."
              className="bg-white"
            />
          </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NewDocumentTemplate;
